import { useEffect, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Minus,
  Music,
  Plus,
  RotateCcw,
} from 'lucide-react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { Media } from './types';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  items: Media[];
  initialIndex: number;
}

function isInlineDocument(mime: string) {
  // Browsers render PDF inline; other office formats need to be downloaded.
  return mime === 'application/pdf';
}

export function MediaViewer({ open, onOpenChange, items, initialIndex }: Props) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(initialIndex);

  // Reset to the requested entry whenever the viewer reopens.
  useEffect(() => {
    if (open) setIndex(initialIndex);
  }, [open, initialIndex]);

  const item = items[index];
  if (!item) return null;

  const canPrev = index > 0;
  const canNext = index < items.length - 1;
  const onPrev = () => canPrev && setIndex(index - 1);
  const onNext = () => canNext && setIndex(index + 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[90vh] w-[min(95vw,1100px)] max-w-none flex-col gap-3 overflow-hidden p-4 sm:p-6"
        showCloseButton
      >
        {/* `pr-8` keeps the counter ("2 / 4") clear of the shadcn close
            button — Dialog renders the X absolutely at right-4, top-4, so
            without padding the two visually crash. */}
        <DialogTitle className="flex items-center justify-between gap-3 pr-8 text-sm font-medium text-muted-foreground">
          <span className="truncate">
            {item.originalName ?? item.url.split('/').pop() ?? t('inspections.media-gallery.file')}
          </span>
          <span className="shrink-0 text-xs tabular-nums">
            {index + 1} / {items.length}
          </span>
        </DialogTitle>

        <div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-lg bg-muted">
          {item.kind === 'photo' ? (
            // Pinch (touch), drag-to-pan, wheel zoom and explicit + / − /
            // reset buttons in the corner. `panning.disabled` defaults to
            // false but only kicks in when scale > 1 (the library
            // protects against dragging the image around when it already
            // fits the canvas). Double-click toggles between 1x and 2x.
            <TransformWrapper
              key={item.url}
              maxScale={6}
              minScale={1}
              initialScale={1}
              wheel={{ step: 0.2 }}
              pinch={{ step: 5 }}
              panning={{ disabled: false, velocityDisabled: false }}
              doubleClick={{ mode: 'toggle', step: 0.7 }}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <div className="absolute right-2 top-2 z-10 flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => zoomOut()}
                      className="size-9 rounded-full bg-background/90 backdrop-blur-sm shadow hover:bg-background"
                      aria-label="Zoom out"
                    >
                      <Minus className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => zoomIn()}
                      className="size-9 rounded-full bg-background/90 backdrop-blur-sm shadow hover:bg-background"
                      aria-label="Zoom in"
                    >
                      <Plus className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => resetTransform()}
                      className="size-9 rounded-full bg-background/90 backdrop-blur-sm shadow hover:bg-background"
                      aria-label="Reset zoom"
                    >
                      <RotateCcw className="size-4" />
                    </Button>
                  </div>
                  <TransformComponent
                    wrapperClass="!size-full"
                    contentClass="!size-full flex items-center justify-center"
                  >
                    <img
                      src={item.url}
                      alt={item.originalName ?? 'photo'}
                      className="max-h-full max-w-full object-contain select-none"
                      draggable={false}
                    />
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          ) : null}

          {item.kind === 'video' ? (
            <video
              key={item.url}
              src={item.url}
              controls
              autoPlay
              className="max-h-full max-w-full"
            />
          ) : null}

          {item.kind === 'audio' ? (
            // Audio is a thin native control — without padding the player
            // sits right next to the glass nav buttons. Wrap it in a tall
            // card with horizontal margin (`mx-16`) so the buttons have
            // breathing room on either side; the music icon adds vertical
            // bulk so the player isn't at the dialog's geometric centre.
            <div className="mx-16 flex w-full max-w-md flex-col items-center gap-5 rounded-2xl border border-border bg-background/80 px-8 py-10 shadow-sm backdrop-blur-sm">
              <div className="flex size-16 items-center justify-center rounded-full bg-primary/10">
                <Music className="size-8 text-primary" />
              </div>
              <p className="text-center text-base font-medium">
                {item.originalName ?? t('inspections.media-gallery.audio')}
              </p>
              <audio key={item.url} src={item.url} controls className="w-full" />
            </div>
          ) : null}

          {item.kind === 'document' ? (
            isInlineDocument(item.mime) ? (
              <iframe
                key={item.url}
                src={item.url}
                title={item.originalName ?? t('inspections.media-gallery.file')}
                className="size-full border-0 bg-background"
              />
            ) : (
              <div className="flex flex-col items-center gap-3 p-6">
                <p className="max-w-md text-center text-sm text-muted-foreground">
                  {t('inspections.media-gallery.preview-unavailable')}
                </p>
                <Button asChild>
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="mr-2 size-4" />
                    {t('inspections.media-gallery.open-new-tab')}
                  </a>
                </Button>
              </div>
            )
          ) : null}

          {items.length > 1 ? (
            <>
              {/* Floating glass buttons over the media — `bg-background/90` +
                  backdrop blur stays high-contrast on whatever colour the
                  photo / video happens to be, where `variant="secondary"`
                  (a muted teal-gray) blended into anything mid-tone. */}
              <Button
                variant="ghost"
                size="icon"
                onClick={onPrev}
                disabled={!canPrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 size-10 rounded-full bg-background/90 backdrop-blur-sm shadow-lg hover:bg-background disabled:opacity-40"
                aria-label={t('inspections.media-gallery.previous')}
              >
                <ChevronLeft className="size-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={onNext}
                disabled={!canNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 size-10 rounded-full bg-background/90 backdrop-blur-sm shadow-lg hover:bg-background disabled:opacity-40"
                aria-label={t('inspections.media-gallery.next')}
              >
                <ChevronRight className="size-5" />
              </Button>
            </>
          ) : null}
        </div>

        <DialogClose className="sr-only">Close</DialogClose>
      </DialogContent>
    </Dialog>
  );
}
